import moment from 'moment';

import { i18n } from '../../../shared/language';

export enum DeclareRegisterInvoiceNextStep {
  VerifyAtTaxAuthority = 1,
  VerifyBiometric = 2,
  VerifyOtp = 3,
  CreateNewDeclaration = 4,
  ExplainRisk = 5,
  Completed = 6,
  SignAndSendToTaxAuthority = 7,
  WaitForTaxAuthorityResponse = 8,
  // Mã 9 là trạng thái Cơ quan Thuế đang kiểm tra sơ bộ tờ khai.
  TaxAuthorityPreliminaryChecking = 9,
}

export type NextStepCode =
  | 'ALL'
  | 'AUTH_DIRECT'
  | 'AUTH_BIOMETRIC'
  | 'AUTH_OTP'
  | 'CREATE_NEW'
  | 'EXPLANATION'
  | 'COMPLETED'
  | 'SIGN_AND_SEND'
  | 'WAIT_FOR_RESPONSE'
  | 'TAX_AUTHORITY_PRELIMINARY_CHECKING'
  | 'UNKNOWN';

export interface DeclarationNextStepApi {
  code: DeclareRegisterInvoiceNextStep;
  label?: string;
  deadline: string | null;
  clickable?: boolean;
}

export interface DeclarationNextStep {
  code?: number | null;
  value: NextStepCode | string;
  label: string;
  action?: string | null;
  guideType?: string;
  clickable: boolean;
  deadline?: string | null;
}

export const NEXT_STEP_FILTER_OPTIONS = [
  // Giá trị bộ lọc phải là số để API nhận đúng tham số nextStepType.
  { value: DeclareRegisterInvoiceNextStep.VerifyAtTaxAuthority, translationKey: 'common.nextStepVerifyAtTaxAuthority' },
  { value: DeclareRegisterInvoiceNextStep.VerifyBiometric, translationKey: 'common.nextStepVerifyBiometric' },
  { value: DeclareRegisterInvoiceNextStep.VerifyOtp, translationKey: 'common.nextStepVerifyOtp' },
  { value: DeclareRegisterInvoiceNextStep.CreateNewDeclaration, translationKey: 'common.nextStepCreateNewDeclaration' },
  { value: DeclareRegisterInvoiceNextStep.ExplainRisk, translationKey: 'common.nextStepExplainRisk' },
  { value: DeclareRegisterInvoiceNextStep.Completed, translationKey: 'common.nextStepCompleted' },
  { value: DeclareRegisterInvoiceNextStep.SignAndSendToTaxAuthority, translationKey: 'common.nextStepSignAndSend' },
  { value: DeclareRegisterInvoiceNextStep.WaitForTaxAuthorityResponse, translationKey: 'common.nextStepWaitForResponse' },
  { value: DeclareRegisterInvoiceNextStep.TaxAuthorityPreliminaryChecking, translationKey: 'common.nextStepPreliminaryChecking' },
];

// Field này được dùng làm tên query param khi DataTable gọi API danh sách.
export const NEXT_STEP_FILTER_FIELD = 'nextStepType';

const asNumber = (value: any): number | null => {
  if (value === undefined || value === null || value === '' || Number.isNaN(Number(value))) {
    return null;
  }
  return Number(value);
};

const asText = (value: any): string => value === undefined || value === null ? '' : String(value);

const buildStep = (value: NextStepCode | string, translationKey: string, fallbackLabel: string, action?: string | null, guideType?: string): DeclarationNextStep => ({
  value,
  // Nhãn bước tiếp theo phải lấy theo locale hiện tại thay vì giữ chuỗi tiếng Việt trong logic mapping.
  label: i18n.t(translationKey, { defaultValue: fallbackLabel }),
  action,
  guideType,
  clickable: Boolean(action),
});

const serverCodeDefinitions: Record<DeclareRegisterInvoiceNextStep, { value: NextStepCode; translationKey: string; fallbackLabel: string; action?: string | null; guideType?: string }> = {
  [DeclareRegisterInvoiceNextStep.VerifyAtTaxAuthority]: { value: 'AUTH_DIRECT', translationKey: 'common.nextStepVerifyAtTaxAuthority', fallbackLabel: 'Xác thực thông tin trực tiếp tại CQT', action: 'VIEW_GUIDANCE', guideType: 'AUTH_DIRECT' },
  [DeclareRegisterInvoiceNextStep.VerifyBiometric]: { value: 'AUTH_BIOMETRIC', translationKey: 'common.nextStepVerifyBiometric', fallbackLabel: 'Xác thực thông tin sinh trắc học', action: 'VIEW_GUIDANCE', guideType: 'AUTH_BIOMETRIC' },
  [DeclareRegisterInvoiceNextStep.VerifyOtp]: { value: 'AUTH_OTP', translationKey: 'common.nextStepVerifyOtp', fallbackLabel: 'Xác thực thông tin OTP', action: 'VIEW_GUIDANCE', guideType: 'AUTH_OTP' },
  [DeclareRegisterInvoiceNextStep.CreateNewDeclaration]: { value: 'CREATE_NEW', translationKey: 'common.nextStepCreateNewDeclaration', fallbackLabel: 'Tạo tờ khai mới', action: 'CLONE_DECLARATION' },
  [DeclareRegisterInvoiceNextStep.ExplainRisk]: { value: 'EXPLANATION', translationKey: 'common.nextStepExplainRisk', fallbackLabel: 'Hướng dẫn giải trình', action: 'VIEW_GUIDANCE', guideType: 'EXPLANATION' },
  [DeclareRegisterInvoiceNextStep.Completed]: { value: 'COMPLETED', translationKey: 'common.nextStepCompleted', fallbackLabel: 'Hoàn thành' },
  [DeclareRegisterInvoiceNextStep.SignAndSendToTaxAuthority]: { value: 'SIGN_AND_SEND', translationKey: 'common.nextStepSignAndSend', fallbackLabel: 'Vui lòng ký & gửi CQT' },
  [DeclareRegisterInvoiceNextStep.WaitForTaxAuthorityResponse]: { value: 'WAIT_FOR_RESPONSE', translationKey: 'common.nextStepWaitForResponse', fallbackLabel: 'Chờ phản hồi từ CQT' },
  [DeclareRegisterInvoiceNextStep.TaxAuthorityPreliminaryChecking]: { value: 'TAX_AUTHORITY_PRELIMINARY_CHECKING', translationKey: 'common.nextStepPreliminaryChecking', fallbackLabel: 'Cơ quan Thuế đang tiến hành kiểm tra sơ bộ tờ khai' },
};

const hasSignedDeclaration = (declaration: any): boolean => (
  asNumber(declaration.signStatus) === 2
);

const hasTaxAuthorityFeedback = (declaration: any): boolean => (
  declaration.taxAuthorityFeedback !== undefined
  && declaration.taxAuthorityFeedback !== null
  && declaration.taxAuthorityFeedback !== ''
);

/** Maps only the BE nextStep contract to the CTA shown in the list. */
export const getDeclarationNextStep = (declaration: any = {}): DeclarationNextStep => {
  const serverStep = declaration.nextStep as DeclarationNextStepApi | null | undefined;
  const code = asNumber(serverStep?.code ?? declaration.nextStepType) as DeclareRegisterInvoiceNextStep | null;
  const definition = code === null ? undefined : serverCodeDefinitions[code];

  if (!definition) {
    if (!serverStep && asNumber(declaration.taxAuthorityFeedback) === 6) {
      return {
        ...buildStep('COMPLETED', 'common.nextStepCompleted', 'Hoàn thành'),
        code: DeclareRegisterInvoiceNextStep.Completed,
        deadline: null,
      };
    }

    if (!serverStep && hasSignedDeclaration(declaration) && !hasTaxAuthorityFeedback(declaration)) {
      return {
        ...buildStep('WAIT_FOR_RESPONSE', 'common.nextStepWaitForResponse', 'Chờ phản hồi từ CQT'),
        code: DeclareRegisterInvoiceNextStep.WaitForTaxAuthorityResponse,
        deadline: null,
      };
    }

    if (!serverStep && !hasSignedDeclaration(declaration)) {
      return {
        ...buildStep('SIGN_AND_SEND', 'common.nextStepSignAndSend', 'Vui lòng ký & gửi CQT'),
        code: DeclareRegisterInvoiceNextStep.SignAndSendToTaxAuthority,
        deadline: null,
      };
    }

    return { ...buildStep('UNKNOWN', 'common.nextStepSignAndSend', 'Vui lòng ký & gửi CQT'), code: null, deadline: null, clickable: false };
  }

  const nextStep = buildStep(
    definition.value,
    definition.translationKey,
    definition.fallbackLabel,
    definition.action,
    definition.guideType,
  );
  const hasAuthenticationDeadline = code === DeclareRegisterInvoiceNextStep.VerifyAtTaxAuthority
    || code === DeclareRegisterInvoiceNextStep.VerifyBiometric
    || code === DeclareRegisterInvoiceNextStep.VerifyOtp;
  const deadline = hasAuthenticationDeadline && serverStep?.deadline ? asText(serverStep.deadline) : null;
  // API thực tế chỉ trả code, label và deadline.
  // Hành động được xác định theo code, không phụ thuộc vào clickable.
  return { ...nextStep, code, deadline, clickable: Boolean(definition.action) };
};

export const formatNextStepDeadline = (value: any): string => {
  if (!value) return '';
  const date = moment(value);
  return date.isValid() ? date.format('DD/MM/YYYY - HH:mm') : asText(value);
};

export const nextStepClassName = (code: string): string => {
  switch (code) {
    case 'AUTH_DIRECT':
    case 'AUTH_BIOMETRIC':
    case 'AUTH_OTP':
    case 'EXPLANATION':
      return 'next-step next-step-warning';
    case 'CREATE_NEW':
      return 'next-step next-step-action';
    case 'SIGN_AND_SEND':
      return 'next-step next-step-sign-and-send';
    case 'WAIT_FOR_RESPONSE':
      return 'next-step next-step-wait-for-response';
    case 'TAX_AUTHORITY_PRELIMINARY_CHECKING':
      // Trạng thái đang kiểm tra sơ bộ dùng cùng màu vàng với xác thực trực tiếp tại CQT.
      return 'next-step next-step-warning';
    case 'COMPLETED':
      return 'next-step next-step-completed';
    default:
      return 'next-step next-step-muted';
  }
};
